<template>
    <div class="quotes-container">
        <div class="quotes-marquee">
            <div class="quotes-content" ref="quotesContent">
                <span
                    v-for="(quote, index) in displayQuotes"
                    :key="index"
                    class="quote-item"
                >
                    <i class="fas fa-quote-left"></i>
                    {{ quote.text }}
                    <i class="fas fa-quote-right"></i>
                    <span class="quote-author">— {{ quote.author }}</span>
                    <i class="fas fa-bicycle quote-separator"></i>
                </span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useCyclingQuotes } from '@/js/composable/useCyclingQuotes.js';
import { computed, onMounted, ref } from 'vue';

// Use the cycling quotes composable
const { currentQuotes } = useCyclingQuotes();

// Reactive refs
const quotesContent = ref(null);

// Duplicate quotes for seamless loop
const displayQuotes = computed(() => {
    if (!currentQuotes.value || currentQuotes.value.length === 0) {
        return [];
    }
    return [...currentQuotes.value, ...currentQuotes.value];
});

onMounted(() => {
    // Start the animation
    if (quotesContent.value) {
        quotesContent.value.style.animationPlayState = 'running';
    }
});
</script>

<style lang="scss" scoped>
@use '@/scss/variables' as vars;

.quotes-container {
    flex: 1;
    max-width: 600px;
    min-width: 200px;
    margin: 0 20px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.2); // Keep visible for now
    border: 2px solid rgba(255, 255, 255, 0.5); // Keep visible for now
    border-radius: 8px;
}

.quotes-marquee {
    height: 40px;
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
}

.quotes-content {
    display: flex;
    align-items: center;
    white-space: nowrap;
    animation: scroll-left 200s linear infinite; // Slower: 60 seconds
    animation-play-state: running;
}

.quote-item {
    display: inline-flex;
    align-items: center;
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
    font-weight: 400;
    margin-right: 40px;
    min-width: max-content;

    .fas.fa-quote-left,
    .fas.fa-quote-right {
        font-size: 10px;
        opacity: 0.7;
        margin: 0 6px;
    }

    .quote-author {
        font-style: italic;
        opacity: 0.8;
        margin-left: 8px;
        font-size: 12px;
    }

    .quote-separator {
        margin-left: 15px;
        font-size: 14px;
        opacity: 0.6;
        color: rgba(255, 255, 255, 0.6);
    }
}

@keyframes scroll-left {
    0% {
        transform: translateX(0%); // Start immediately visible
    }
    100% {
        transform: translateX(-50%); // Move to end position
    }
}

// Pause animation on hover for better readability
.quotes-container:hover .quotes-content {
    animation-play-state: paused;
}

// Extra smooth animation with hardware acceleration
.quotes-content {
    will-change: transform;
    backface-visibility: hidden;
    perspective: 1000px;
}
</style>
